<template>
  <v-container fluid class="px-sm-10">
    <v-row>
      <v-col cols="auto" class="headline">Overview</v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-form ref="form" @submit.prevent="submit" v-model="valid">
          <v-card class="mb-12">
            <v-card-title>Shop Information</v-card-title>
            <v-card-text>
              <v-text-field name="shopname" label="Title" v-model="ownOverview.name" :rules="stringValidation" :disabled="isSaving"/>
              <v-text-field name="orgname" label="Organisation Name" v-model="ownOverview.org_name" :disabled="isSaving"/>
              <v-text-field name="orgnumber" label="Organisation Number" type="tel" v-model="ownOverview.org_number" :disabled="isSaving"/>
              <v-text-field name="vatnumber" label="VAT Registration Number" type="tel" v-model="ownOverview.vat_number" :disabled="isSaving"/>
              <v-textarea auto-grow rows="1" name="contact" label="Contact" type="tel" v-model="ownOverview.contact" :disabled="isSaving"/>
              <image-input
                label="Image"
                v-model="image"
                :crop-width="450"
                :crop-height="450"
                :crop-clamp="true"
                :quality="0.8"
                ouput-type="jpeg"
                class="mt-0"
                :disabled="isSaving"
              ></image-input>
            </v-card-text>

            <v-card-title>Messages</v-card-title>
            <v-card-text>
              <v-textarea
                auto-grow
                rows="1"
                name="startdialog"
                label="Open message (Shown in a dialog when opening the shop)"
                v-model="ownOverview.message_startup"
                :disabled="isSaving"
              />
              <v-textarea
                auto-grow
                rows="1"
                name="message_order_received"
                label="Message for 'Order received'"
                v-model="ownOverview.message_order_received"
                :disabled="isSaving"
                messages="Available to insert: amount, dateCreated, datePaid, operatorName, deliveryTime"
                class="mb-8"
              />
              <v-textarea
                auto-grow
                rows="1"
                name="message_order_pickup"
                label="Message for 'Order ready to pick up'"
                v-model="ownOverview.message_order_pickup"
                placeholder="Order klar, avhämtas vid #{deviceName}"
                :disabled="isSaving"
                messages="Available to insert: deviceName, amount, dateCreated, datePaid, operatorName, deliveryTime"
              />
            </v-card-text>

            <v-card-text>
              <v-expansion-panels flat hover multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0 title">Financial settings</v-expansion-panel-header>
                  <v-expansion-panel-content>

                    <h3 class="mb-2 text--secondary">Z-Report</h3>

                    <time-input
                      label="Take Z-Report at"
                      v-model="ownOverview.zreport_time"
                      placeholder="03:00"
                      messages="Time in day that Z-Report will be created. Rounded to nearest full or half hour"
                    ></time-input>

                    <h3 class="mt-8 mb-2 text--secondary">Financial year</h3>

                    <p class="mb-4 text--secondary">
                      Set start and end date of your financial year.
                      You can leave this empty if your financial year starts 1:st of January and ends 31:st of December
                    </p>

                    <date-input
                      label="Financial year start"
                      v-model="ownOverview.financial_year_start"
                      :placeholder="`${currentYear}-01-01`"
                      :persistent-placeholder="true"
                    ></date-input>

                    <date-input
                      label="Financial year end"
                      v-model="ownOverview.financial_year_end"
                      :placeholder="`${currentYear}-12-31`"
                      :persistent-placeholder="true"
                    ></date-input>

                    <h3 class="mt-4 mb-2 text--secondary">Accounts per payment type</h3>

                    <p class="mb-4 text--secondary">Accounts to use for each payment type when exporting data to an SIE-file.</p>

                    <v-text-field
                      label="Swish"
                      :value="(ownOverview.payment_type_account_numbers || {}).SWISH"
                      @input="objectInput('payment_type_account_numbers', 'SWISH', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="App Card Payment"
                      :value="(ownOverview.payment_type_account_numbers || {}).CARDCHECKOUT"
                      @input="objectInput('payment_type_account_numbers', 'CARDCHECKOUT', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="IZettle"
                      :value="(ownOverview.payment_type_account_numbers || {}).IZETTLE"
                      @input="objectInput('payment_type_account_numbers', 'IZETTLE', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Bambora"
                      :value="(ownOverview.payment_type_account_numbers || {}).BAMBORA"
                      @input="objectInput('payment_type_account_numbers', 'BAMBORA', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Gift Cards"
                      :value="(ownOverview.payment_type_account_numbers || {}).GIFTCARD"
                      @input="objectInput('payment_type_account_numbers', 'GIFTCARD', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-4 mb-2 text--secondary">Accounts per VAT rate</h3>

                    <p class="mb-4 text--secondary">Accounts to use for each VAT rate when exporting data to an SIE-file.</p>

                    <v-text-field
                      label="25% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['25']"
                      @input="objectInput('vat_account_numbers', '25', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="12% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['12']"
                      @input="objectInput('vat_account_numbers', '12', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="6% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['6']"
                      @input="objectInput('vat_account_numbers', '6', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-4 mb-2 text--secondary">Rounding Account</h3>

                    <p class="mb-4 text--secondary">Account to use for rounding in order for exports to balance.</p>

                    <v-text-field
                      label="Rounding Account"
                      v-model="ownOverview.rounding_account_number"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0 title">Advanced</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3 class="mb-2 text--secondary">Online point of sale</h3>

                    <p class="mb-4 text--secondary">Settings for the online point of sale used for purchases made from the app.</p>

                    <v-text-field
                      label="Online point of sale Id"
                      v-model="ownOverview.online_point_of_sale_id"
                      placeholder="FTP001"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Online POS control unit Id"
                      v-model="ownOverview.online_pos_control_unit_id"
                      placeholder="RIHTT..."
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Online POS manufacturing number"
                      v-model="ownOverview.online_pos_manufacturer_number"
                      autocomplete="off"
                    ></v-text-field>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>

            <v-divider />

            <v-card-title>
              <v-btn depressed color="primary" type="submit" :disabled="!valid" :loading="isSaving">Save</v-btn>
            </v-card-title>
          </v-card>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {ImageInput} from 'dync-imageinput';
import {DateInput, TimeInput} from 'dync-datetime';

export default {
  name: 'Overview',
  data () {
    return {
      ownOverview: {},
      imageName: 'dync-appshop-header.jpg',
      image: null,
      hadImage: false,
      valid: true,
      stringValidation: [v => !!v || 'Cannot be empty'],
      currentYear: (new Date()).getFullYear()
    }
  },
  watch: {
    overview (newVal) {
      this.setOwnOverview(newVal);
    }
  },
  computed: {
    isSaving () {
      return this.$store.getters.isLoadingFor(['overviews.update', 'overviews.create']);
    },
    overview () {
      const items = this.$store.getters['overviews/items'];
      return items.length > 0 ? items[0] : null;
    }
  },
  methods: {
    submit () {
      const item = Object.assign({}, this.ownOverview);

      if (this.image instanceof Blob) {
        item.attachments = [{
          name: this.imageName,
          file: this.image
        }];
      }
      if (!this.image && this.hadImage) {
        item.attachments_remove = [this.imageName];
      }


      var promise;
      if (this.ownOverview.id) {
        promise = this.$store.dispatch('overviews/updateItem', item);
      } else {
        promise = this.$store.dispatch('overviews/createItem', item);
      }
      promise.then(() => {
        this.$snackbar({message: 'Overview saved!', theme: 'success'});
      }).catch((error) => {
        console.error(error);
        this.$snackbar({message: 'Could not save overview', theme: 'error'});
      })
    },
    setOwnOverview (overview) {
      this.image = null;
      this.ownOverview = Object.assign({}, overview);
      this.hadImage = false;
      if (this.ownOverview.attachment_names.indexOf(this.imageName) != -1) {
        this.hadImage = true;
        this.image = this.$store.getters['overviews/getAttachmentUrl'](overview.id, this.imageName);
      }
    },
    objectInput (key, objectKey, value) {
      var item = {...(this.ownOverview[key] || {})};
      item[objectKey] = value;
      this.$set(this.ownOverview, key, item);
    }
  },
  mounted () {
    if (this.overview) {
      this.setOwnOverview(this.overview);
    }
  },
  components: {
    ImageInput,
    DateInput,
    TimeInput
  }
}
</script>

<style lang="scss" scoped>
  .overview__image-uploader {
    height: 500px;
  }
</style>
